import { useCallback } from 'react';

import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import {
  DEFAULT_BUMPOUT_DEPTH,
  DEFAULT_BUMPOUT_LENGTH,
  DEFAULT_CORNER_RADIUS,
  DEFAULT_EXTERNAL_DIAGONAL_CORNER_LENGTH,
  DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH,
  DEFAULT_RADIUS_SAGITTA,
  INCH_QUOTE,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { CustomSideType, DiagonalCornerOptions } from '~/core/enums';
import { getCornerType } from '~/core/helpers';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import {
  CornerInfo,
  CornerRadiusModel,
  DiagonalCornerModel,
  DrawingEdge,
  DrawingShape,
  getMinMaxFromCorner,
  isCornerRadiusModel,
} from '~/core/models';
import { CustomSideModel } from '~/core/models/predefined-custom-side.model';
import {
  ensureMutable,
  ensureMutableArray,
  forceAssignArrayElement,
  forceAssignProperty,
} from '~/core/utils/objectMutability.utils';

export const useCanvasSelectionHandler = () => {
  const { cornerRadii } = useCornerRadii();
  const { registerCustomSideAction, registerCornerAction } =
    useRegisterUserAction();

  /**
   * update a cornerInfo record with new radius corner product data
   * will remove diagonal length, and set the labels
   * WARNING: MUTATES corner Object
   * @param corner
   * @param product
   * @param size use undefined to select the default value from the product, otherwise this size is used
   */
  const setCornerRadiusSize = useCallback(
    (corner: CornerInfo, size: number): void =>
      drawingTool.setCornerRadiusSize(corner, size, cornerRadii),
    [cornerRadii]
  );

  const updateCornerRecordWithDiagonalProduct = (
    corner: CornerInfo,
    product: DiagonalCornerModel
  ): void => {
    corner.diagonalLength =
      (corner.angle ?? 0) > 0
        ? DEFAULT_EXTERNAL_DIAGONAL_CORNER_LENGTH
        : DEFAULT_INTERNAL_DIAGONAL_CORNER_LENGTH;
    corner.productId = product.productId;
    corner.label = product.name ?? '';
    corner.labelMeasure?.set('text', `${corner.diagonalLength}${INCH_QUOTE}`);
    corner.labelText?.set('text', corner.label);
  };

  const removeDiagonalCornerOnShape = useCallback(
    (corner: CornerInfo) => setCornerRadiusSize(corner, DEFAULT_CORNER_RADIUS),
    [setCornerRadiusSize]
  );

  const handleCornerSelection = useCallback(
    (
      corner: CornerInfo,
      selectedOption: CornerRadiusModel | DiagonalCornerModel
    ): void => {
      const cornerType = getCornerType(selectedOption);

      if (isCornerRadiusModel(selectedOption)) {
        const radius = getMinMaxFromCorner(selectedOption);
        const intendedRadius = radius.max;
        const appliedSize = Math.min(corner.maxAllowedRadius ?? 0, radius.max);

        // Store the intended radius on the corner object before applying constraints.
        // This value will be read later by getConstrainedSize() to detect if the radius
        // was constrained to a lower value than intended, triggering the modal notification.
        corner.intendedRadius = intendedRadius;

        setCornerRadiusSize(corner, appliedSize);

        registerCornerAction('cornerRadius');
      } else {
        if (
          cornerType === DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER &&
          corner.diagonalLength
        ) {
          removeDiagonalCornerOnShape(corner);
        } else {
          updateCornerRecordWithDiagonalProduct(corner, selectedOption);
        }
        registerCornerAction(cornerType);
      }
    },
    [removeDiagonalCornerOnShape, setCornerRadiusSize, registerCornerAction]
  );

  const handleCustomSideBy: Record<
    CustomSideType,
    (edge: DrawingEdge, shape?: DrawingShape) => void
  > = {
    [CustomSideType.RADIUS_OUT]: (edge, shape) => {
      const mutableEdge = ensureMutable(edge);

      forceAssignProperty(
        mutableEdge,
        'customSideType',
        CustomSideType.RADIUS_OUT
      );
      forceAssignProperty(mutableEdge, 'radiusSagitta', DEFAULT_RADIUS_SAGITTA);

      if (mutableEdge !== edge && shape) {
        const mutableArray = ensureMutableArray(shape.drawingEdges);

        if (mutableArray !== shape.drawingEdges) {
          forceAssignProperty(shape, 'drawingEdges', mutableArray);
        }

        const edgeIndexInArray = mutableArray.findIndex(
          (e: DrawingEdge) => e.id === edge.id
        );

        if (edgeIndexInArray !== -1) {
          forceAssignArrayElement(mutableArray, edgeIndexInArray, mutableEdge);
        }
      }
    },
    [CustomSideType.BUMP_OUT]: (edge, shape) => {
      const mutableEdge = ensureMutable(edge);

      forceAssignProperty(
        mutableEdge,
        'customSideType',
        CustomSideType.BUMP_OUT
      );

      if (mutableEdge.bumpOut) {
        const mutableBumpOut = ensureMutable(mutableEdge.bumpOut);

        forceAssignProperty(mutableBumpOut, 'length', DEFAULT_BUMPOUT_LENGTH);
        forceAssignProperty(mutableBumpOut, 'depth', DEFAULT_BUMPOUT_DEPTH);

        if (mutableBumpOut !== mutableEdge.bumpOut) {
          forceAssignProperty(mutableEdge, 'bumpOut', mutableBumpOut);
        }
      }

      if (mutableEdge !== edge && shape) {
        const mutableArray = ensureMutableArray(shape.drawingEdges);

        if (mutableArray !== shape.drawingEdges) {
          forceAssignProperty(shape, 'drawingEdges', mutableArray);
        }

        const edgeIndexInArray = mutableArray.findIndex(
          (e: DrawingEdge) => e.id === edge.id
        );

        if (edgeIndexInArray !== -1) {
          forceAssignArrayElement(mutableArray, edgeIndexInArray, mutableEdge);
        }
      }
    },
    [CustomSideType.REMOVE_CUSTOM_SIDE]: (edge, shape) => {
      const mutableEdge = ensureMutable(edge);

      forceAssignProperty(mutableEdge, 'customSideType', undefined);
      forceAssignProperty(mutableEdge, 'realRadius', undefined);

      if (mutableEdge.bumpOut) {
        const mutableBumpOut = ensureMutable(mutableEdge.bumpOut);
        const bumpOutProperties = [
          'length',
          'depth',
          'clockwiseRemainderLength',
          'counterClockwiseRemainderLength',
        ];

        bumpOutProperties.forEach((prop) => {
          forceAssignProperty(
            mutableBumpOut,
            prop as keyof typeof mutableBumpOut,
            0
          );
        });

        if (mutableBumpOut !== mutableEdge.bumpOut) {
          forceAssignProperty(mutableEdge, 'bumpOut', mutableBumpOut);
        }
      }

      if (mutableEdge !== edge && shape) {
        const mutableArray = ensureMutableArray(shape.drawingEdges);

        if (mutableArray !== shape.drawingEdges) {
          forceAssignProperty(shape, 'drawingEdges', mutableArray);
        }

        const edgeIndexInArray = mutableArray.findIndex(
          (e: DrawingEdge) => e.id === edge.id
        );

        if (edgeIndexInArray !== -1) {
          forceAssignArrayElement(mutableArray, edgeIndexInArray, mutableEdge);
        }
      }
    },
  };

  const handleEdgeSelection = (
    edge: DrawingEdge,
    shape: DrawingShape,
    selectedOption: CustomSideModel
  ) => {
    const handleUnknownSide = () => {
      const startAndEndCorners = Object.values(shape.cornerMap).filter(
        ({ id }) => [edge.startCornerId, edge.endCornerId].includes(id)
      );

      (startAndEndCorners.length === 2 ? startAndEndCorners : []).forEach(
        (corner) => {
          corner.label = DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER;
          corner.productId = undefined;
          corner.size = DEFAULT_CORNER_RADIUS;
          removeDiagonalCornerOnShape(corner);
        }
      );
    };
    const sideType =
      selectedOption.type === edge.customSideType
        ? CustomSideType.REMOVE_CUSTOM_SIDE
        : selectedOption.type;
    const updateCustomSide = handleCustomSideBy[sideType] ?? handleUnknownSide;

    updateCustomSide(edge, shape);
    registerCustomSideAction(sideType);
  };

  return {
    handleCornerSelection,
    handleEdgeSelection,
    removeDiagonalCornerOnShape,
    setCornerRadiusSize,
  };
};
